import { EnrollmentAssessmentDtoStatusEnum, type EnglishTestDto } from '@/api/generated/models'
import type { EnglishTestResult } from './types'

export const testPoints = [
  { what: 'Read road signs', why: 'Required by FMCSA guidelines' },
  { what: 'Understand instructions', why: 'Takes about 10 minutes' }
]

const MAX_ATTEMPTS_REACHED_MESSAGE = 'Maximum attempts reached for this test'
const RETRY_IN_24_HOURS_MESSAGE = 'You must wait 24 hours before retrying this test'
const RETRY_IN_30_DAYS_MESSAGE = 'You must wait 30 days before retrying this test'

export const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = error.response

    if (
      typeof response === 'object' &&
      response !== null &&
      'data' in response &&
      typeof response.data === 'object' &&
      response.data !== null &&
      'message' in response.data &&
      typeof response.data.message === 'string'
    ) {
      return response.data.message
    }
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallbackMessage
}

export const formatStartTestErrorMessage = (error: unknown, fallbackMessage: string) => {
  const errorMessage = getErrorMessage(error, fallbackMessage)

  if (errorMessage.includes(MAX_ATTEMPTS_REACHED_MESSAGE)) {
    return `${errorMessage}. Please try again in 24 hours`
  }

  return errorMessage
}

export const isRetryLockoutMessage = (message: string) =>
  message.includes(RETRY_IN_24_HOURS_MESSAGE) || message.includes(RETRY_IN_30_DAYS_MESSAGE)

export const createPassedAssessmentResult = (
  assessment: {
    id?: number | null
    status?: string | null
    passed?: boolean | null
    score?: number | null
    passingScore?: number | null
  },
  defaultTest: EnglishTestDto
): EnglishTestResult => ({
  assessmentId: assessment.id ?? null,
  status: assessment.status ?? null,
  passed: assessment.passed === true,
  score: Number(assessment.score ?? 0),
  passingScore: Number(assessment.passingScore ?? defaultTest.passingScore ?? 0),
  answers: []
})

export const getPersistedEnglishTestResult = (enrollment?: unknown): EnglishTestResult | null => {
  const enrollmentRecord = enrollment as Record<string, unknown> | null | undefined
  const enrollmentStatus =
    typeof enrollmentRecord?.status === 'string' ? enrollmentRecord.status.toUpperCase() : ''
  const englishTestCompletedAt =
    typeof enrollmentRecord?.englishTestCompletedAt === 'string' &&
    enrollmentRecord.englishTestCompletedAt.trim().length > 0
      ? enrollmentRecord.englishTestCompletedAt
      : null
  const enrollmentTestCompleted =
    enrollmentRecord?.englishTestCompleted === true || englishTestCompletedAt !== null
  const enrollmentTestPassed = enrollmentRecord?.englishTestPassed === true
  const enrollmentScore =
    typeof enrollmentRecord?.englishTestScore === 'number' &&
    Number.isFinite(enrollmentRecord.englishTestScore)
      ? enrollmentRecord.englishTestScore
      : null
  const passedAssessment = enrollmentStatus === 'ASSESSMENT_PASSED'
  const failedAssessment = enrollmentStatus === 'ASSESSMENT_FAILED'
  const hasCompletedAssessmentState =
    enrollmentTestCompleted ||
    enrollmentTestPassed ||
    passedAssessment ||
    failedAssessment ||
    enrollmentScore !== null

  if (!hasCompletedAssessmentState) {
    return null
  }

  const inferredPassed = enrollmentTestPassed || passedAssessment

  return {
    assessmentId: null,
    status: inferredPassed
      ? EnrollmentAssessmentDtoStatusEnum.Passed
      : EnrollmentAssessmentDtoStatusEnum.Failed,
    passed: inferredPassed,
    score: Number(enrollmentScore ?? 0),
    passingScore: 0,
    answers: []
  }
}
